import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const sel = "h-9 rounded-md border border-stone-200 bg-white px-2 text-sm";

const SortableList = ({ items, onReorder, render }) => {
  const [drag, setDrag] = useState(null);
  const drop = (target) => {
    if (drag === null || drag === target) return;
    const next = [...items];
    const [moved] = next.splice(drag, 1);
    next.splice(target, 0, moved);
    setDrag(null);
    onReorder(next);
  };
  return items.map((it, i) => (
    <div key={it.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
      className={`flex items-center gap-2 rounded-md border bg-white px-2 py-1.5 ${drag === i ? "border-amber-500 opacity-60" : "border-stone-200"}`} data-testid={`category-item-${it.id}`}>
      <GripVertical className="h-4 w-4 cursor-grab text-stone-400" />{render(it)}
    </div>
  ));
};

const CatActions = ({ c, tops, onSave, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(c.name);
  const [parent, setParent] = useState(c.parent_id || "");
  if (!edit) return (
    <>
      <span className="flex-1 text-sm">{c.name} <span className="text-xs text-stone-400">({c.id})</span></span>
      <Button size="icon" variant="ghost" onClick={() => setEdit(true)} data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={() => onDelete(c)} data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-700" /></Button>
    </>
  );
  return (
    <>
      <Input className="h-8 flex-1" value={name} onChange={(e) => setName(e.target.value)} data-testid={`category-name-input-${c.id}`} />
      <select className={sel} value={parent} onChange={(e) => setParent(e.target.value)}>
        <option value="">(Kategori utama)</option>
        {tops.filter((t) => t.id !== c.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <Button size="sm" onClick={async () => { if (await onSave(c, { name, parent_id: parent || null })) setEdit(false); }} data-testid={`category-save-${c.id}`}>Simpan</Button>
    </>
  );
};

const Categories = () => {
  const call = useApi();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const tops = cats.filter((c) => !c.parent_id);

  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const add = (e) => { e.preventDefault(); run(() => call("post", "/admin/categories", { name, parent_id: parent || null }), "Kategori ditambahkan").then((ok) => ok && setName("")); };
  const save = (c, body) => run(() => call("put", `/admin/categories/${c.id}`, body), "Kategori diperbarui");
  const del = (c) => window.confirm(`Hapus kategori "${c.name}"?`) && run(() => call("delete", `/admin/categories/${c.id}`), "Kategori dihapus");
  const reorder = (list) => { setCats((s) => [...list, ...s.filter((x) => !list.some((l) => l.id === x.id))]); run(() => call("post", "/admin/categories/reorder", { ids: list.map((x) => x.id) }), "Urutan disimpan"); };
  const row = (c) => <CatActions key={c.id + c.name + c.parent_id} c={c} tops={tops} onSave={save} onDelete={del} />;

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4" data-testid="categories-section">
      <h2 className="font-semibold">Kategori</h2>
      <p className="mb-3 text-xs text-stone-500">Seret ikon titik-titik untuk mengubah urutan tampil di toko. Kategori utama tampil sebagai tab di halaman depan.</p>
      <form onSubmit={add} className="mb-4 flex flex-wrap gap-2">
        <Input required placeholder="Nama kategori baru" className="w-56" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-category-name" />
        <select className={sel} value={parent} onChange={(e) => setParent(e.target.value)} data-testid="new-category-parent">
          <option value="">Sebagai kategori utama</option>
          {tops.map((t) => <option key={t.id} value={t.id}>Sub-kategori dari {t.name}</option>)}
        </select>
        <Button type="submit" data-testid="add-category-button"><Plus className="mr-1 h-4 w-4" />Tambah</Button>
      </form>
      <div className="space-y-2">
        <SortableList items={tops} onReorder={reorder} render={(t) => (
          <div className="flex-1">
            <div className="flex items-center gap-2">{row(t)}</div>
            {cats.some((c) => c.parent_id === t.id) && (
              <div className="ml-4 mt-2 space-y-1.5 border-l-2 border-stone-100 pl-3">
                <SortableList items={cats.filter((c) => c.parent_id === t.id)} onReorder={reorder} render={row} />
              </div>
            )}
          </div>
        )} />
      </div>
    </section>
  );
};

const Tags = () => {
  const call = useApi();
  const [tags, setTags] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/tags").then((r) => setTags(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-lg border border-stone-200 bg-white p-4" data-testid="tags-section">
      <h2 className="mb-3 font-semibold">Tag / Label</h2>
      <form onSubmit={(e) => { e.preventDefault(); run(() => call("post", "/admin/tags", { name }), "Tag ditambahkan").then(() => setName("")); }} className="mb-4 flex gap-2">
        <Input required placeholder="mis. Baru, Bestseller" className="w-56" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-tag-name" />
        <Button type="submit" data-testid="add-tag-button"><Plus className="mr-1 h-4 w-4" />Tambah</Button>
      </form>
      <div className="flex flex-wrap gap-2">
        {tags.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-stone-200 py-1 pl-3 pr-1 text-sm" data-testid={`tag-chip-${t.id}`}>
            {t.name}
            <button title="Ubah nama" className="rounded-full p-1 hover:bg-stone-100" data-testid={`tag-edit-${t.id}`} onClick={() => { const n = window.prompt("Nama tag", t.name); if (n && n.trim()) run(() => call("put", `/admin/tags/${t.id}`, { name: n }), "Tag diperbarui"); }}><Pencil className="h-3 w-3" /></button>
            <button title="Hapus" className="rounded-full p-1 hover:bg-red-50" data-testid={`tag-delete-${t.id}`} onClick={() => window.confirm(`Hapus tag "${t.name}"? Tag juga dilepas dari semua produk.`) && run(() => call("delete", `/admin/tags/${t.id}`), "Tag dihapus")}><Trash2 className="h-3 w-3 text-red-700" /></button>
          </span>
        ))}
      </div>
    </section>
  );
};

export default function AdminCategories() {
  return (
    <div className="space-y-6">
      <PageHeader title="Kategori & Tag" />
      <div className="grid gap-6 lg:grid-cols-2"><Categories /><Tags /></div>
    </div>
  );
}
